
import React from "react";
import { Button } from "@/components/ui/button";
import { CalculatorInput } from "@/components/ui/calculator-input";
import { Label } from "@/components/ui/label";
import StepCard from "../StepCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, AlertTriangle, HelpCircle, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { CALIBRATION_KEYS, CalibrationMetadata } from "@/constants/calibrationKeys";

interface PressureAdvanceFastModeProps {
  pressureAdvanceStart: string;
  onPressureAdvanceStartChange: (value: string) => void;
  pressureAdvanceStep: string;
  onPressureAdvanceStepChange: (value: string) => void;
  measuredHeight: string;
  onMeasuredHeightChange: (value: string) => void;
  tunedPressureAdvance: string;
  onGoToTutorial: () => void;
}

const PressureAdvanceFastMode = ({
  pressureAdvanceStart,
  onPressureAdvanceStartChange,
  pressureAdvanceStep,
  onPressureAdvanceStepChange,
  measuredHeight,
  onMeasuredHeightChange,
  tunedPressureAdvance,
  onGoToTutorial
}: PressureAdvanceFastModeProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const formatWithOriginalSeparator = (value: string, originalInput: string) => {
    if (!value) return value;
    
    let usedSeparator = '.';
    if (originalInput.includes(',')) usedSeparator = ',';
    else if (originalInput.includes("'")) usedSeparator = "'";
    else if (originalInput.includes('٫')) usedSeparator = '٫';
    
    if (usedSeparator !== '.') {
      return value.replace('.', usedSeparator);
    }
    
    return value;
  };

  const trimTrailingZeros = (value: string) => {
    if (!value) return value;
    
    // Convert to number and back to string to remove trailing zeros
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    
    return num.toString();
  };

  const getDisplayResult = () => {
    if (!tunedPressureAdvance) return "";
    // Use the first available input to determine the separator format
    const referenceInput = pressureAdvanceStart || pressureAdvanceStep || measuredHeight;
    const trimmedValue = trimTrailingZeros(tunedPressureAdvance);
    return formatWithOriginalSeparator(trimmedValue, referenceInput);
  };

  const handleBackToTools = () => {
    navigate('/tools');
  };

  const handleGoToTutorial = () => {
    onGoToTutorial();
  };

  const handleCopyPA = () => {
    if (tunedPressureAdvance) {
      const formattedValue = getDisplayResult();
      navigator.clipboard.writeText(formattedValue);
      
      // Mark calibration as completed
      const calibrationData: CalibrationMetadata = {
        completed: true,
        completedAt: new Date().toISOString(),
        slicerUsed: "fast-mode" // Default for fast mode since we don't track specific slicer
      };
      localStorage.setItem(CALIBRATION_KEYS.pressureAdvance, JSON.stringify(calibrationData));
      
      toast({
        title: "Copied!",
        description: `PA value ${formattedValue} copied to clipboard. Calibration marked as complete.`,
        duration: 3000
      });
    }
  };

  const getPAStatus = () => {
    if (!tunedPressureAdvance) return "neutral";
    const value = parseFloat(tunedPressureAdvance);
    // Updated range: 0.02 to 0.07 for typical PA values
    if (value > 0.07 || value < 0.02) return "warning";
    return "good";
  };

  return (
    <StepCard 
      title=""
      showPrevious={false}
      showNext={false}
    >
      <div className="space-y-6">
        <div className="space-y-4">
          {/* Desktop layout for PA Start and PA Step */}
          <div className="hidden md:block">
            {/* Labels row */}
            <div className="grid grid-cols-2 gap-4 mb-2">
              <Label htmlFor="pressure-advance-start" className="text-base font-semibold">
                PA Start
              </Label>
              <Label htmlFor="pressure-advance-step" className="text-base font-semibold">
                PA Step
              </Label>
            </div>
            {/* Inputs row */}
            <div className="grid grid-cols-2 gap-4">
              <CalculatorInput
                placeholder=""
                value={pressureAdvanceStart}
                onChange={onPressureAdvanceStartChange}
                id="pressure-advance-start"
                autoFocus
                allowNegative={false}
                className="border-gray-300 dark:border-gray-600"
              />
              <CalculatorInput
                placeholder=""
                value={pressureAdvanceStep}
                onChange={onPressureAdvanceStepChange}
                id="pressure-advance-step"
                allowNegative={false}
                className="border-gray-300 dark:border-gray-600"
              />
            </div>
          </div>

          {/* Mobile layout - stacked */}
          <div className="md:hidden space-y-4">
            <CalculatorInput
              label="PA Start"
              placeholder=""
              value={pressureAdvanceStart}
              onChange={onPressureAdvanceStartChange}
              id="pressure-advance-start-mobile"
              autoFocus
              allowNegative={false}
              className="border-gray-300 dark:border-gray-600"
            />

            <CalculatorInput
              label="PA Step"
              placeholder=""
              value={pressureAdvanceStep}
              onChange={onPressureAdvanceStepChange}
              id="pressure-advance-step-mobile"
              allowNegative={false}
              className="border-gray-300 dark:border-gray-600"
            />
          </div>

          <CalculatorInput
            label="Best height (mm)"
            placeholder=""
            value={measuredHeight}
            onChange={onMeasuredHeightChange}
            id="measured-height"
            allowNegative={false}
            className="border-gray-300 dark:border-gray-600"
          />
        </div>

        {tunedPressureAdvance && (
          <div className={`p-4 rounded-lg border-2 ${getPAStatus() === "warning" ? "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800" : "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-600"}`}>
            <div className="space-y-3">
              <div className="text-center">
                <div className="text-base font-semibold mb-2">New PA Value</div>
                <div className={`text-3xl font-bold ${getPAStatus() === "warning" ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}>
                  {getDisplayResult()}
                </div>
              </div>
              
              <div className="flex justify-center">
                <Button 
                  onClick={handleCopyPA} 
                  variant="outline" 
                  size="sm" 
                  className="border-gray-300 dark:border-gray-600"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>
          </div>
        )}

        {getPAStatus() === "warning" && tunedPressureAdvance && (
          <div className="p-4 rounded-lg border-2 bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
            <div className="flex items-center justify-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span className="text-center">Warning: This value is outside the typical recommended range.</span>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={handleBackToTools}
              variant="outline"
              className="min-h-[44px] border-gray-300 dark:border-gray-600"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            <Button
              onClick={handleGoToTutorial}
              variant="outline"
              className="min-h-[44px] border-gray-300 dark:border-gray-600"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Tutorial Mode
            </Button>
          </div>
        </div>
      </div>
    </StepCard>
  );
};

export default PressureAdvanceFastMode;
